import {useNavigate, useParams} from 'react-router-dom';
import {useEffect, useRef, useState} from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {useLazyPrintOrderQuery} from '../../redux/DepotSlice/depotOrdersApi';
import logo from '../../assets/img/clogo5.png';
import styles from './InvoicePDFView.module.css';
const InvoicePdfView = () => {
	const {id} = useParams();
	const navigate = useNavigate();
	const pdfRef = useRef<HTMLDivElement | null>(null);
	const [trigger, {data, isLoading, isError}] = useLazyPrintOrderQuery();

	useEffect(() => {
		if (id) {
			trigger(+id);
		}
	}, [id, trigger]);

	const [pdfGenerated, setPdfGenerated] = useState(false);

	useEffect(() => {
		if (!isLoading && data?.order && !pdfGenerated) {
			const generatePdf = async () => {
				// Ждем загрузки всех элементов
				await new Promise<void>((resolve) => {
					setTimeout(() => {
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								resolve();
							});
						});
					}, 100);
				});

				if (!pdfRef.current) {
					return;
				}
				
				try {
					const canvas = await html2canvas(pdfRef.current, {
						scale: 1.5,
						backgroundColor: '#ffffff',
						logging: false,
						useCORS: false,
						allowTaint: false,
						onclone: () => {
							// Отключаем логи в клонированном документе
							if (window.console && window.console.log) {
								window.console.log = () => {};
							}
						},
					});

					const imgData = canvas.toDataURL('image/png', 0.95);

					const pdf = new jsPDF({
						orientation: 'portrait',
						unit: 'mm',
						format: 'a4',
					});

					const pageWidth = pdf.internal.pageSize.getWidth();
					const pageHeight = pdf.internal.pageSize.getHeight();
					const imgProps = pdf.getImageProperties(imgData);
					const ratio = Math.min(
						pageWidth / imgProps.width,
						pageHeight / imgProps.height,
					);
					const imgWidth = imgProps.width * ratio;
					const imgHeight = imgProps.height * ratio;

					pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

					const pdfBlob = pdf.output('blob');
					const pdfUrl = URL.createObjectURL(pdfBlob);
					window.open(pdfUrl, '_blank');

					setPdfGenerated(true);

					// Ждем больше времени, чтобы PDF успел открыться
					setTimeout(() => {
						navigate(-1);
					}, 1000);
				} catch (error) {
					console.error('Ошибка генерации PDF:', error);
					navigate(-1);
				}
			};
			generatePdf();
		}
	}, [data, isLoading, pdfGenerated]);

	return (
		<div className={styles.pdfInvoice} ref={pdfRef}>
			{isLoading && <div className={styles.pdfLoading}><p>Загрузка...</p></div>}
			{isError && <div className={styles.pdfError}><p>Ошибка загрузки данных</p></div>}
			{!isLoading && !isError && !data && <div className={styles.pdfNoData}><p>Нет данных для отображения</p></div>}
			{data?.order &&
				data.order?.map((order) => (
					<div key={order.id}>
						{/* Header */}
						<div className={styles.pdfHeader}>
							<div>
								<img
									src={logo}
									alt="Choice Logo"
									className={styles.pdfLogo}
								/>
							</div>
							<div className={styles.pdfCompanyInfo}>
								<div>
									<strong>Choice</strong>
								</div>
								<div>Система управління замовленнями</div>
								<div>cabinet.choice.ua</div>
							</div>
						</div>

						{/* Title */}
						<div className={styles.pdfTitle}>
							Накладна #{order.id}
						</div>

						{/* Order Information */}
						<div className={styles.pdfOrderInfo}>
							<div className={styles.pdfInfoSection}>
								<div className={styles.pdfInfoTitle}>
									Інформація про замовлення
								</div>
								<div className={styles.pdfInfoItem}>
									<span className={styles.pdfInfoLabel}>
										ID замовлення:
									</span>
									<span className={styles.pdfInfoValue}>
										{order.id}
									</span>
								</div>
								<div className={styles.pdfInfoItem}>
									<span className={styles.pdfInfoLabel}>
										Дата проведення:
									</span>
									<span className={styles.pdfInfoValue}>
										{order.order_date}
									</span>
								</div>
								<div className={styles.pdfInfoItem}>
									<span className={styles.pdfInfoLabel}>
										Дата створення:
									</span>
									<span className={styles.pdfInfoValue}>
										{order.create_date}
									</span>
								</div>
								<div className={styles.pdfInfoItem}>
									<span className={styles.pdfInfoLabel}>
										Вид накладної:
									</span>
									<span className={styles.pdfInfoValue}>
										{order.type}
									</span>
								</div>
								<div className={styles.pdfInfoItem}>
									<span className={styles.pdfInfoLabel}>
										Замовник:
									</span>
									<span className={styles.pdfInfoValue}>
										{order.initial_name}
									</span>
								</div>
								<div className={styles.pdfInfoItem}>
									<span className={styles.pdfInfoLabel}>
										Склад обслуговування:
									</span>
									<span className={styles.pdfInfoValue}>
										{order.ware_name}
									</span>
								</div>
							</div>

							<div className={styles.pdfInfoSection}>
								<div className={styles.pdfInfoTitle}>
									Контактна інформація
								</div>
								<div className={styles.pdfInfoItem}>
									<span className={styles.pdfInfoLabel}>
										Отримувач:
									</span>
									<span className={styles.pdfInfoValue}>
										{order.contact_name}
									</span>
								</div>
								<div className={styles.pdfInfoItem}>
									<span className={styles.pdfInfoLabel}>
										Телефон:
									</span>
									<span className={styles.pdfInfoValue}>
										{order.contact_phone}
									</span>
								</div>
							</div>
						</div>

						{/* Products Table */}
						{order.products && order.products.length > 0 && (
							<>
								<div className={styles.pdfProductsTitle}>
									Товари
								</div>
								<table className={styles.pdfProductsTable}>
									<thead>
										<tr className={styles.pdfTableHeader}>
											<th className={`${styles.pdfTableHeaderCell} ${styles.pdfTableHeaderCellNumber}`}>
												№
											</th>
											<th className={`${styles.pdfTableHeaderCell} ${styles.pdfTableHeaderCellPhoto}`}>
												Фото
											</th>
											<th className={styles.pdfTableHeaderCell}>
												Назва товару
											</th>
											<th className={`${styles.pdfTableHeaderCell} ${styles.pdfTableHeaderCellQuantity}`}>
												Кількість
											</th>
											<th className={`${styles.pdfTableHeaderCell} ${styles.pdfTableHeaderCellPrice}`}>
												Ціна
											</th>
											<th className={`${styles.pdfTableHeaderCell} ${styles.pdfTableHeaderCellSum}`}>
												Сума
											</th>
										</tr>
									</thead>
									<tbody>
										{order.products.map((product, index) => (
											<tr
												key={product.id}
												className={index % 2 === 0 ? styles.pdfTableRow : styles.pdfTableRowAlt}
											>
												<td className={styles.pdfTableCell}>
													{index + 1}
												</td>
												<td className={styles.pdfTableCell}>
													{product.href && (
														<img
															src={product.href}
															alt={product.name}
															className={styles.pdfProductImage}
														/>
													)}
												</td>
												<td className={`${styles.pdfTableCell} ${styles.pdfProductName}`}>
													{product.name}
												</td>
												<td className={styles.pdfTableCell}>
													{product.count}
												</td>
												<td className={styles.pdfTableCell}>
													{product.money} {order.currency}
												</td>
												<td className={styles.pdfTableCell}>
													{product.count * product.money} {order.currency}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</>
						)}

						{/* Totals */}
						<div className={styles.pdfTotals}>
							<div className={styles.pdfTotalRow}>
								<span className={styles.pdfTotalLabel}>
									Всього балів:
								</span>
								<span className={styles.pdfTotalValue}>
									{order.sum_points} Б
								</span>
							</div>
							<div className={styles.pdfTotalRow}>
								<span className={styles.pdfTotalLabel}>
									Всього грошей:
								</span>
								<span className={styles.pdfTotalValue}>
									{order.sum_money} {order.currency}
								</span>
							</div>
							<div className={`${styles.pdfTotalRow} ${styles.pdfTotalFinal}`}>
								<span className={styles.pdfTotalLabel}>
									Загальна сума:
								</span>
								<span className={styles.pdfTotalValue}>
									{order.sum_money} {order.currency}
								</span>
							</div>
						</div>

						{/* Footer */}
						<div className={styles.pdfFooter}>
							<div>Документ згенеровано автоматично</div>
							<div>
								Дата генерації: {new Date().toLocaleDateString('uk-UA')}
							</div>
							<div>© Choice - Система управління замовленнями</div>
						</div>
					</div>
				))}
		</div>
	);
};

export default InvoicePdfView;
